import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { MagnifyingGlass, FunnelSimple } from "@phosphor-icons/react";
import { api, CATEGORIES, PRODUCT_TYPES } from "../lib/api";

import { BeerCard } from "../components/BeerCard";

export default function Catalog() {
  const location = useLocation();
  const [beers, setBeers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [type, setType] = useState("todos");
  const [category, setCategory] = useState("todas");
  const [style, setStyle] = useState("todos");
  const [search, setSearch] = useState("");

  // Cargar productos
  const loadBeers = () => {
    setLoading(true);
    setError(false);
    api.get("/beers")
      .then((res) => setBeers(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBeers();
  }, [location.key]);

  // Reiniciar filtros según la URL en cada navegación al catálogo
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tipo");
    const c = params.get("cat");
    setType(t && PRODUCT_TYPES.some((p) => p.value === t) ? t : "todos");
    setCategory(c && CATEGORIES.some((x) => x.value === c) ? c : "todas");
    setStyle("todos");
    setSearch("");
  }, [location.key, location.search]);

  const styles = useMemo(() => {
    const pool = type === "todos" ? beers : beers.filter((b) => b.product_type === type);
    return ["todos", ...Array.from(new Set(pool.map((b) => b.style))).sort()];
  }, [beers, type]);

  const availableTypes = useMemo(() => {
    const present = new Set(beers.map((b) => b.product_type));
    return PRODUCT_TYPES.filter((t) => t.value === "todos" || present.has(t.value));
  }, [beers]);

  const filtered = beers.filter((b) => {
    if (type !== "todos" && b.product_type !== type) return false;
    if (category !== "todas" && b.category !== category) return false;
    if (style !== "todos" && b.style !== style) return false;
    if (search && !(b.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
      <div className="mb-8">
        <div className="overline text-[#F5A623] mb-2">Calidad artesanal · del producto a tu boca</div>
        <h1 className="font-display text-5xl md:text-7xl leading-none">Catálogo</h1>
        <p className="text-gray-400 mt-3">Cerveza que es arte: artesanal GARAJE e importadas del mundo. Y la Boutique de Garaje: ropa, cristalería, packs de regalo y té.</p>
      </div>

      {/* Product type selector */}
      {availableTypes.length > 2 && (
        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-4" data-testid="type-selector">
          {availableTypes.map((t) => (
            <button
              key={t.value}
              onClick={() => { setType(t.value); setStyle("todos"); }}
              className={`px-5 py-2.5 uppercase tracking-widest text-sm border transition-colors ${
                type === t.value ? "bg-[#D97736] border-[#D97736] text-black font-semibold" : "border-white/15 text-gray-300 hover:border-[#D97736] hover:text-[#D97736]"
              }`}
              data-testid={`type-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar filters */}
        <aside className="lg:col-span-1 lg:sticky lg:top-28 h-fit space-y-8" data-testid="catalog-filters">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar producto..."
              className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 pl-10 text-white placeholder:text-gray-600"
              data-testid="search-input"
            />
          </div>

          <div>
            <div className="overline mb-3 flex items-center gap-2"><FunnelSimple size={14} /> Categoría</div>
            <div className="flex flex-col">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  className={`text-left py-2.5 border-b border-white/5 uppercase tracking-widest text-sm transition-colors ${
                    category === c.value ? "text-[#D97736]" : "text-gray-400 hover:text-white"
                  }`}
                  data-testid={`filter-category-${c.value}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-3">Estilo</div>
            <div className="flex flex-wrap gap-2">
              {styles.map((s) => (
                <button
                  key={s}
                  onClick={() => setStyle(s)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wide border transition-colors ${
                    style === s ? "border-[#D97736] text-[#D97736]" : "border-white/15 text-gray-400 hover:border-white/40"
                  }`}
                  data-testid={`filter-style-${s}`}
                >
                  {s === "todos" ? "Todos" : s}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-3">
          <div className="text-gray-500 text-sm mb-5" data-testid="results-count">
            {filtered.length} {filtered.length === 1 ? "resultado" : "resultados"}
          </div>
          {type === "boutique" && (
            <div className="mb-6 border border-[#D97736]/40 bg-[#D97736]/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid="boutique-coming-soon">
              <span className="font-display text-2xl text-[#D97736] leading-none">¡Pronto más productos!</span>
              <span className="text-gray-300 text-sm">Gorras, cristalería cervecera y cajas de regalo vienen en camino. Franelas y suéteres en tallas S, M, L y XL.</span>
            </div>
          )}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="catalog-loading">
              <div className="w-12 h-12 border-[3px] border-[#F5A623] border-t-transparent rounded-full animate-spin" />
              <p className="text-[#F5A623] mt-5 text-lg font-medium tracking-wide">Cargando catálogo...</p>
              <p className="text-gray-400 mt-1 text-sm">Un momento, estamos trayendo nuestras cervezas.</p>
            </div>
          ) : error ? (
            <div className="text-center py-24 border border-white/10 bg-[#141414]" data-testid="catalog-error">
              <p className="text-gray-300 mb-1">No pudimos cargar el catálogo.</p>
              <p className="text-gray-500 text-sm mb-6">Revisa tu conexión e intenta de nuevo.</p>
              <button
                onClick={loadBeers}
                className="px-6 py-2.5 bg-[#D97736] text-black font-semibold uppercase tracking-widest text-sm hover:bg-[#F5A623] transition-colors"
                data-testid="catalog-retry"
              >
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 border border-white/10 bg-[#141414]">
              <p className="text-gray-400">No encontramos cervezas con esos filtros.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((b, i) => (
                <BeerCard key={b.id} beer={b} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
